import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Users, MousePointerClick, Percent, Pencil, ExternalLink } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import WeeklyReport from "../../components/admin/WeeklyReport";
import { errorMessage, pagesApi } from "../../lib/api";

const Kpi = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="border border-border bg-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="display mt-3 text-5xl leading-none">{value}</p>
    {sub && <p className="mt-2 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Breakdown = ({ title, rows, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-border bg-card p-5" data-testid={testId}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{title}</p>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No data yet</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {rows.map((r) => (
            <li key={r.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate pr-3 font-medium">{r.name}</span>
                <span className="tabular-nums text-muted-foreground">{r.count}</span>
              </div>
              <div className="mt-1.5 h-1 w-full bg-secondary">
                <div className="h-1 bg-primary transition-[width] duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-border bg-card px-3 py-2 text-xs">
      <p className="font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-muted-foreground">
          {p.name}: <span className="text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const PageStats = () => {
  const { id } = useParams();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    pagesApi
      .stats(id, Number(days))
      .then(setData)
      .catch((err) => toast.error(errorMessage(err, "Could not load stats")));
  }, [id, days]);

  const daily = (data?.daily || []).map((d) => ({ ...d, label: d.date.slice(5) }));

  return (
    <div className="space-y-6" data-testid="page-stats">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
          </Link>
          <h1 className="display mt-3 text-4xl uppercase leading-none tracking-wide md:text-5xl" data-testid="stats-title">
            {data?.page?.title || <Skeleton className="inline-block h-10 w-64" />}
          </h1>
          {data?.page?.slug && <p className="mt-1 text-sm text-muted-foreground">/lp/{data.page.slug}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-10 w-40 rounded-none border-border bg-transparent" data-testid="stats-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-border bg-card">
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button asChild variant="outline" className="rounded-none">
            <Link to={`/admin/pages/${id}`} data-testid="stats-edit-link">
              <Pencil className="mr-2 h-4 w-4" /> Edit page
            </Link>
          </Button>
          {data?.page?.slug && (
            <Button asChild variant="outline" className="rounded-none">
              <a href={`/lp/${data.page.slug}`} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" /> Open
              </a>
            </Button>
          )}
        </div>
      </div>

      {!data ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {["a", "b", "c", "d"].map((k) => (
            <Skeleton key={k} className="h-28" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Eye} label="Views" value={data.period.views} sub={`${data.totals.views} all time`} testId="stat-views" />
            <Kpi icon={Users} label="Unique visitors" value={data.period.unique_visitors} sub={`${data.totals.unique_visitors} all time`} testId="stat-uniques" />
            <Kpi icon={MousePointerClick} label="CTA clicks" value={data.period.clicks} sub={`${data.totals.clicks} all time`} testId="stat-clicks" />
            <Kpi icon={Percent} label="Click-through" value={`${data.period.ctr}%`} sub={`${data.totals.ctr}% all time`} testId="stat-ctr" />
          </div>

          <WeeklyReport wow={data.week_over_week} weekly={data.weekly} />

          <div className="border border-border bg-card p-5" data-testid="stats-chart">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Daily views &amp; clicks</p>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <ChartTooltip content={<ChartTip />} cursor={{ stroke: "hsl(var(--border))" }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="clicks" name="Clicks" stroke="hsl(var(--foreground))" strokeWidth={1.5} fill="url(#gClicks)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Breakdown title="UTM sources" rows={data.sources} testId="stats-sources" />
            <Breakdown title="Campaigns" rows={data.campaigns} testId="stats-campaigns" />
            <Breakdown title="Devices" rows={data.devices} testId="stats-devices" />
            <Breakdown title="Referrers" rows={data.referrers} testId="stats-referrers" />
            <Breakdown title="CTA clicks by button" rows={data.ctas} testId="stats-ctas" />
          </div>

          <div className="border border-border bg-card" data-testid="stats-recent">
            <div className="border-b border-border px-5 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Recent activity</p>
            </div>
            {data.recent.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">No events yet. Share your campaign link to start collecting data.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-[0.2em]">When</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.2em]">Event</TableHead>
                    <TableHead className="hidden text-[10px] uppercase tracking-[0.2em] md:table-cell">Source / Campaign</TableHead>
                    <TableHead className="hidden text-[10px] uppercase tracking-[0.2em] md:table-cell">Device</TableHead>
                    <TableHead className="hidden text-[10px] uppercase tracking-[0.2em] lg:table-cell">Referrer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-sm text-muted-foreground">{new Date(e.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">
                        <span className={e.type === "click" ? "font-semibold text-primary" : ""}>{e.type}</span>
                        {e.cta_label && <span className="text-muted-foreground"> · {e.cta_label}</span>}
                      </TableCell>
                      <TableCell className="hidden text-sm md:table-cell">{[e.utm_source, e.utm_campaign].filter(Boolean).join(" / ") || "direct"}</TableCell>
                      <TableCell className="hidden text-sm capitalize md:table-cell">{e.device}</TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">{e.referrer || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PageStats;
